import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { motion } from "framer-motion";
import { Plus, X, Users, Mail, Phone, Building2, Star, MessageSquare, Send } from "lucide-react";
import { toast } from "sonner";

const STAGES = ["lead", "qualified", "engaged", "negotiation", "active", "dormant"];
const CATEGORIES = ["client", "investor", "government", "partner", "referral", "consultant", "vendor"];

const fmtMoney = (n) => {
  if (!n) return "—";
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(0)}K`;
  return `$${n}`;
};

const CATEGORY_COLOR = {
  client: "#22C55E", investor: "#D4AF37", government: "#3B82F6",
  partner: "#A855F7", referral: "#F59E0B", consultant: "#06B6D4", vendor: "#94A3B8",
};

export default function CRM() {
  const [items, setItems] = useState([]);
  const [view, setView] = useState("kanban");
  const [filterCat, setFilterCat] = useState("");
  const [openNew, setOpenNew] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [drawerTab, setDrawerTab] = useState("details");
  const [communications, setCommunications] = useState([]);
  const [commForm, setCommForm] = useState({ type: "note", direction: "outbound" });

  const refresh = async () => {
    const r = await api.get("/stakeholders");
    setItems(r.data);
  };
  useEffect(() => { refresh(); }, []);

  const filtered = useMemo(
    () => (filterCat ? items.filter((i) => i.category === filterCat) : items),
    [items, filterCat]
  );

  const grouped = useMemo(() => {
    const g = {}; STAGES.forEach((s) => (g[s] = []));
    filtered.forEach((i) => g[i.stage]?.push(i));
    return g;
  }, [filtered]);

  const save = async () => {
    if (!form.name || !form.organization || !form.category) {
      toast.error("Name, organization, and category are required.");
      return;
    }
    try {
      if (editing) {
        await api.put(`/stakeholders/${editing.id}`, form);
        toast.success("Stakeholder updated");
      } else {
        await api.post("/stakeholders", form);
        toast.success("Stakeholder added");
      }
      setOpenNew(false); setEditing(null); setForm({}); refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const removeItem = async (id) => {
    if (!window.confirm("Remove this stakeholder?")) return;
    try {
      await api.delete(`/stakeholders/${id}`);
      refresh(); toast.success("Removed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const updateStage = async (id, stage) => {
    try {
      await api.put(`/stakeholders/${id}`, { stage });
      refresh();
    } catch (err) {
      console.error("Failed to update stakeholder stage", err);
      toast.error(err?.response?.data?.detail || "Could not update stage");
    }
  };

  // Load communications when editing
  useEffect(() => {
    if (editing) {
      api.get(`/communications?stakeholder_id=${editing.id}`).then((r) => setCommunications(r.data));
    } else {
      setCommunications([]); setDrawerTab("details");
    }
  }, [editing]);

  const addCommunication = async () => {
    if (!commForm.content?.trim()) { toast.error("Content required"); return; }
    try {
      await api.post("/communications", { ...commForm, stakeholder_id: editing.id });
      const r = await api.get(`/communications?stakeholder_id=${editing.id}`);
      setCommunications(r.data);
      setCommForm({ type: "note", direction: "outbound" });
      toast.success("Communication logged");
    } catch (e) { toast.error("Failed"); }
  };

  const removeCommunication = async (cid) => {
    await api.delete(`/communications/${cid}`);
    const r = await api.get(`/communications?stakeholder_id=${editing.id}`);
    setCommunications(r.data);
  };

  return (
    <div className="p-8 space-y-6" data-testid="crm-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="label-eyebrow mb-2">// CRM // Relationship Engine</div>
          <h1 className="font-display text-4xl font-medium text-white tracking-tight">Stakeholders</h1>
          <p className="text-slate-400 mt-2 text-sm">Clients, investors, government, partners and the long tail.</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex border border-white/10">
            <button
              onClick={() => setView("kanban")}
              data-testid="crm-view-kanban"
              className={`px-4 py-2 font-mono text-xs tracking-widest ${view === "kanban" ? "bg-[#D4AF37] text-[#050B14]" : "text-slate-400 hover:text-white"}`}
            >KANBAN</button>
            <button
              onClick={() => setView("list")}
              data-testid="crm-view-list"
              className={`px-4 py-2 font-mono text-xs tracking-widest ${view === "list" ? "bg-[#D4AF37] text-[#050B14]" : "text-slate-400 hover:text-white"}`}
            >LIST</button>
          </div>
          <select
            value={filterCat}
            onChange={(e) => setFilterCat(e.target.value)}
            data-testid="crm-category-filter"
            className="px-3 py-2 bg-[#0A111F] border border-white/10 text-white font-mono text-xs focus:border-[#D4AF37] outline-none"
          >
            <option value="">ALL CATEGORIES</option>
            {CATEGORIES.map((c) => <option key={c} value={c}>{c.toUpperCase()}</option>)}
          </select>
          <button
            onClick={() => { setEditing(null); setForm({ category: "client", stage: "lead", relationship_score: 50 }); setOpenNew(true); }}
            data-testid="add-stakeholder-btn"
            className="px-4 py-2 flex items-center gap-2 font-medium text-sm"
            style={{ background: "var(--gold-500)", color: "#050B14" }}
          >
            <Plus className="w-4 h-4" /> Add Stakeholder
          </button>
        </div>
      </div>

      {view === "kanban" ? (
        <div className="grid grid-cols-6 gap-3 overflow-x-auto pb-4 min-w-max lg:min-w-0">
          {STAGES.map((stage) => (
            <div key={stage} className="min-w-[240px]" data-testid={`stage-${stage}`}>
              <div className="flex items-center justify-between mb-3 px-2">
                <div className="label-eyebrow">{stage}</div>
                <span className="font-mono text-xs text-slate-500">{grouped[stage].length}</span>
              </div>
              <div className="space-y-2 max-h-[70vh] overflow-y-auto pr-1">
                {grouped[stage].map((s) => (
                  <motion.div
                    layout
                    key={s.id}
                    onClick={() => { setEditing(s); setForm(s); setOpenNew(true); }}
                    data-testid={`stakeholder-card-${s.id}`}
                    className="nexus-card p-3 cursor-pointer"
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <span className="w-1.5 h-1.5" style={{ background: CATEGORY_COLOR[s.category] }}></span>
                      <span className="label-eyebrow">{s.category}</span>
                      <span className="ml-auto font-mono text-[10px] text-[#D4AF37]">{s.relationship_score}</span>
                    </div>
                    <div className="text-sm text-white font-medium truncate">{s.name}</div>
                    <div className="text-[11px] text-slate-500 truncate mt-0.5">{s.organization}</div>
                    {s.value > 0 && (
                      <div className="mt-2 font-mono text-xs" style={{ color: "var(--gold-400)" }}>{fmtMoney(s.value)}</div>
                    )}
                    <div className="mt-2 flex gap-1">
                      {STAGES.filter((x) => x !== s.stage).slice(0, 3).map((target) => (
                        <button
                          key={target}
                          onClick={(e) => { e.stopPropagation(); updateStage(s.id, target); }}
                          className="text-[9px] font-mono px-1.5 py-0.5 border border-white/10 hover:border-[#D4AF37]/50 hover:text-[#D4AF37] text-slate-500 tracking-widest uppercase"
                        >→{target.slice(0, 3)}</button>
                      ))}
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="nexus-card overflow-hidden" data-testid="crm-table">
          <table className="w-full">
            <thead>
              <tr className="border-b border-white/10 text-left">
                {["Name", "Organization", "Category", "Stage", "Score", "Value", ""].map((h) => (
                  <th key={h} className="px-4 py-3 label-eyebrow">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((s) => (
                <tr key={s.id} className="border-b border-white/5 hover:bg-[#10182B] transition-colors cursor-pointer"
                    onClick={() => { setEditing(s); setForm(s); setOpenNew(true); }}>
                  <td className="px-4 py-3 text-sm text-white">{s.name}</td>
                  <td className="px-4 py-3 text-sm text-slate-400">{s.organization}</td>
                  <td className="px-4 py-3">
                    <span className="font-mono text-[10px] uppercase tracking-widest" style={{ color: CATEGORY_COLOR[s.category] }}>{s.category}</span>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs uppercase tracking-widest text-slate-400">{s.stage}</td>
                  <td className="px-4 py-3 font-mono text-sm" style={{ color: "var(--gold-400)" }}>{s.relationship_score}</td>
                  <td className="px-4 py-3 font-mono text-sm text-white">{fmtMoney(s.value)}</td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={(e) => { e.stopPropagation(); removeItem(s.id); }} className="text-slate-500 hover:text-red-400">
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Form drawer */}
      {openNew && (
        <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center bg-black/60" onClick={() => setOpenNew(false)}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="w-full max-w-3xl bg-[#0A111F] border border-white/10 max-h-[92vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            data-testid="stakeholder-form"
          >
            <div className="flex justify-between items-start p-8 pb-0">
              <div>
                <div className="label-eyebrow mb-1">// {editing ? "Stakeholder" : "New Stakeholder"}</div>
                <h2 className="font-display text-2xl text-white">{form.name || "Untitled Relationship"}</h2>
                {editing && <div className="text-sm text-slate-400 mt-1">{form.organization}</div>}
              </div>
              <button onClick={() => setOpenNew(false)} className="text-slate-500 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>

            {editing && (
              <div className="flex border-b border-white/10 mt-6 px-8">
                {[
                  { id: "details", label: "Details" },
                  { id: "communications", label: `Communications (${communications.length})`, icon: MessageSquare },
                ].map((t) => (
                  <button
                    key={t.id}
                    onClick={() => setDrawerTab(t.id)}
                    data-testid={`drawer-tab-${t.id}`}
                    className={`px-4 py-3 text-xs font-mono tracking-widest uppercase border-b-2 ${drawerTab === t.id ? "text-white border-[#D4AF37]" : "text-slate-500 border-transparent hover:text-white"}`}
                  >{t.label}</button>
                ))}
              </div>
            )}

            <div className="p-8">
              {drawerTab === "details" || !editing ? (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    {[
                      ["name", "Full name", "text", true],
                      ["organization", "Organization", "text", true],
                      ["title", "Title", "text"],
                      ["email", "Email", "email"],
                      ["phone", "Phone", "text"],
                      ["value", "Engagement value ($)", "number"],
                    ].map(([k, label, type, req]) => (
                      <div key={k}>
                        <label className="label-eyebrow block mb-1.5">{label}{req && " *"}</label>
                        <input
                          data-testid={`field-${k}`}
                          type={type}
                          value={form[k] ?? ""}
                          onChange={(e) => setForm({ ...form, [k]: type === "number" ? +e.target.value : e.target.value })}
                          className="w-full px-3 py-2 bg-[#050B14] border border-white/10 text-white font-mono text-sm focus:border-[#D4AF37] outline-none"
                        />
                      </div>
                    ))}
                    <div>
                      <label className="label-eyebrow block mb-1.5">Category *</label>
                      <select
                        data-testid="field-category"
                        value={form.category || ""}
                        onChange={(e) => setForm({ ...form, category: e.target.value })}
                        className="w-full px-3 py-2 bg-[#050B14] border border-white/10 text-white font-mono text-sm focus:border-[#D4AF37] outline-none"
                      >
                        <option value="">— select —</option>
                        {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="label-eyebrow block mb-1.5">Stage</label>
                      <select
                        data-testid="field-stage"
                        value={form.stage || "lead"}
                        onChange={(e) => setForm({ ...form, stage: e.target.value })}
                        className="w-full px-3 py-2 bg-[#050B14] border border-white/10 text-white font-mono text-sm focus:border-[#D4AF37] outline-none"
                      >
                        {STAGES.map((c) => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </div>
                    <div className="col-span-2">
                      <label className="label-eyebrow block mb-1.5">Relationship score (0-100)</label>
                      <input
                        type="range" min={0} max={100}
                        value={form.relationship_score ?? 50}
                        onChange={(e) => setForm({ ...form, relationship_score: +e.target.value })}
                        className="w-full"
                      />
                      <div className="font-mono text-sm mt-1" style={{ color: "var(--gold-400)" }}>{form.relationship_score ?? 50}</div>
                    </div>
                    <div className="col-span-2">
                      <label className="label-eyebrow block mb-1.5">Notes</label>
                      <textarea
                        value={form.notes || ""}
                        onChange={(e) => setForm({ ...form, notes: e.target.value })}
                        rows={4}
                        className="w-full px-3 py-2 bg-[#050B14] border border-white/10 text-white font-mono text-sm focus:border-[#D4AF37] outline-none resize-none"
                      />
                    </div>
                  </div>
                  <div className="mt-6 flex justify-end gap-3">
                    <button onClick={() => setOpenNew(false)} className="px-5 py-2 border border-white/10 text-slate-300 hover:border-white/30">Cancel</button>
                    <button
                      onClick={save}
                      data-testid="save-stakeholder-btn"
                      className="px-5 py-2 font-medium"
                      style={{ background: "var(--gold-500)", color: "#050B14" }}
                    >{editing ? "Save Changes" : "Add Stakeholder"}</button>
                  </div>
                </>
              ) : (
                <div data-testid="communications-tab">
                  {/* Add new communication */}
                  <div className="grid grid-cols-3 gap-3 mb-4">
                    <select
                      value={commForm.type}
                      onChange={(e) => setCommForm({ ...commForm, type: e.target.value })}
                      className="px-3 py-2 bg-[#050B14] border border-white/10 text-white font-mono text-xs focus:border-[#D4AF37] outline-none"
                    >
                      {["note", "email", "call", "meeting", "message"].map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                    <select
                      value={commForm.direction}
                      onChange={(e) => setCommForm({ ...commForm, direction: e.target.value })}
                      className="px-3 py-2 bg-[#050B14] border border-white/10 text-white font-mono text-xs focus:border-[#D4AF37] outline-none"
                    >
                      <option value="outbound">outbound</option>
                      <option value="inbound">inbound</option>
                      <option value="internal">internal</option>
                    </select>
                    <input
                      value={commForm.subject || ""}
                      onChange={(e) => setCommForm({ ...commForm, subject: e.target.value })}
                      placeholder="Subject"
                      className="px-3 py-2 bg-[#050B14] border border-white/10 text-white font-mono text-xs focus:border-[#D4AF37] outline-none"
                    />
                  </div>
                  <textarea
                    data-testid="comm-content"
                    rows={3}
                    value={commForm.content || ""}
                    onChange={(e) => setCommForm({ ...commForm, content: e.target.value })}
                    placeholder="What happened / what was said?"
                    className="w-full px-3 py-2 bg-[#050B14] border border-white/10 text-white font-mono text-xs focus:border-[#D4AF37] outline-none resize-none"
                  />
                  <div className="flex justify-end mt-2">
                    <button
                      onClick={addCommunication}
                      data-testid="log-comm-btn"
                      className="px-4 py-2 flex items-center gap-2 text-sm font-medium"
                      style={{ background: "var(--gold-500)", color: "#050B14" }}
                    >
                      <Send className="w-3.5 h-3.5" /> Log
                    </button>
                  </div>

                  {/* Timeline */}
                  <div className="mt-6 space-y-3 max-h-[400px] overflow-y-auto pr-2">
                    {communications.map((c) => (
                      <div key={c.id} className="relative pl-6 group">
                        <div className="absolute left-0 top-1.5 w-2 h-2" style={{ background: "var(--gold-500)" }}></div>
                        <div className="absolute left-[3.5px] top-3 bottom-[-12px] w-px bg-white/10"></div>
                        <div className="flex items-center gap-2 mb-1">
                          <span className="label-eyebrow">{c.type} · {c.direction}</span>
                          <span className="font-mono text-[10px] text-slate-500 ml-auto">
                            {new Date(c.created_at).toLocaleString("en-US", { hour12: false })}
                          </span>
                          <button onClick={() => removeCommunication(c.id)} className="opacity-0 group-hover:opacity-100 text-slate-500 hover:text-red-400">
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                        {c.subject && <div className="text-sm text-white">{c.subject}</div>}
                        <div className="text-xs text-slate-400 mt-1 leading-relaxed">{c.content}</div>
                        <div className="text-[10px] text-slate-600 font-mono mt-1">— {c.actor_name}</div>
                      </div>
                    ))}
                    {!communications.length && (
                      <div className="text-center text-xs text-slate-500 py-8">No communications logged yet.</div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}
